import { StoreSubject } from "../../../../src/lib/store";

export type PerformancePending =
  | { kind: "launch"; patternId: string }
  | { kind: "stop" };

export type PerformanceLane = {
  /** Pattern currently feeding this track slot (null = silence). */
  activePatternId: string | null;
  /** Queued change, applied at the next pattern-end (lane period boundary). */
  pending: PerformancePending | null;
};

export type PerformanceState = {
  playing: boolean;
  /** Live master-bus capture of this Performance session. */
  recording: boolean;
  lanes: PerformanceLane[];
  /** Local row inside the assembled PERF pattern. */
  playheadRow: number | null;
  quantumSteps: number;
};

function emptyLane(): PerformanceLane {
  return { activePatternId: null, pending: null };
}

export function createPerformanceLanes(trackCount: number): PerformanceLane[] {
  const n = Math.max(1, Math.floor(trackCount));
  return Array.from({ length: n }, () => emptyLane());
}

const initial: PerformanceState = {
  playing: false,
  recording: false,
  lanes: createPerformanceLanes(8),
  playheadRow: null,
  quantumSteps: 16,
};

export const performanceStore = new StoreSubject<PerformanceState>(initial);

export function patchPerformance(partial: Partial<PerformanceState>): void {
  performanceStore.next({ ...performanceStore.getValue(), ...partial });
}

export function resizePerformanceLanes(trackCount: number): PerformanceLane[] {
  const prev = performanceStore.getValue().lanes;
  const n = Math.max(1, Math.floor(trackCount));
  const next = Array.from({ length: n }, (_, i) => prev[i] ?? emptyLane());
  patchPerformance({ lanes: next });
  return next;
}

/** Reorder performance lanes in lockstep with instrument tracks. */
export function reorderPerformanceLanes(fromIndex: number, toIndex: number): void {
  const lanes = performanceStore.getValue().lanes;
  if (
    fromIndex < 0
    || toIndex < 0
    || fromIndex >= lanes.length
    || toIndex >= lanes.length
    || fromIndex === toIndex
  ) {
    return;
  }
  const next = [...lanes];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) {
    return;
  }
  next.splice(toIndex, 0, moved);
  patchPerformance({ lanes: next });
}
